import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ActionIcon, Badge, Group, Stack, Table, Text, Tooltip } from "@mantine/core";
import { openConfirmModal } from "@mantine/modals";
import { IconPencil, IconTrash, IconUsers } from "@tabler/icons-react";

import { PermissionGate } from "@components/PermissionGate";
import {
  TableEmptyRow,
  TableErrorRow,
  TableSkeletonRows,
} from "@components/Table";

import { formatDateToString } from "@utils/dateValidation";
import { renderLoadingErrorData } from "@utils/renderLoadingErrorData";

import { useDeleteMasterclass } from "@hooks/mutations/masterclass/useDeleteMasterclass";

import { Masterclass } from "@customTypes/masterclass";


interface MasterclassTableProps {
  data: Masterclass[] | undefined;
  isLoading: boolean;
  isError: boolean;
  isSuccess: boolean;
  isRefetching: boolean;
  onEdit: (masterclass: Masterclass) => void;
  onViewEnrollments: (masterclass: Masterclass) => void;
}

export const MasterclassTable = ({
  data,
  isLoading,
  isError,
  isSuccess,
  isRefetching,
  onEdit,
  onViewEnrollments,
}: MasterclassTableProps) => {
  const { t, i18n } = useTranslation();
  const { mutate: deleteMasterclass } = useDeleteMasterclass();

  const confirmDelete = (id: string, name: string) => {
    openConfirmModal({
      title: t("delete-masterclass"),
      children: (
        <Text size="sm">
          {t("confirm-delete-masterclass", { name })}
        </Text>
      ),
      labels: { confirm: t("delete"), cancel: t("cancel") },
      confirmProps: { color: 'red' },
      onConfirm: () => deleteMasterclass(id),
    });
  };

  const rows = useMemo(() => {
    return data?.map(item => (
      <Table.Tr key={item._id}>
        <Table.Td>
          <Stack gap={0}>
            <Text fz={14} fw={500} lh={1.5}>
              {item.name}
            </Text>
            <Text fz={12} c="dimmed">
              {item.slug}
            </Text>
          </Stack>
        </Table.Td>

        <Table.Td>
          <Badge
            variant="outline"
            color={item.mode === 'in_person' ? 'orange' : 'blue'}
            tt="capitalize"
          >
            {t((item.mode || "online").replace("_", "-"))}
          </Badge>
        </Table.Td>

        <Table.Td>
          <Stack gap={0}>
            <Text fz={14} lh={1.5}>
              {formatDateToString(new Date(item.datetime))}
            </Text>
            <Text fz={13} c="dimmed" lh={1.5}>
              {new Date(item.datetime).toLocaleTimeString(i18n.language, {
                hour: "2-digit",
                minute: "2-digit",
              })}
            </Text>
          </Stack>
        </Table.Td>

        <Table.Td>
          <Text fz={14} fw={500}>
            {Intl.NumberFormat(i18n.language, {
              style: "currency",
              currency: "CFA",
            }).format(item.price)}
          </Text>
        </Table.Td>

        <Table.Td>
          <Group gap={5}>
            <Text fz={14} fw={500}>{item.enrollment_count}</Text>
            <Text fz={14} c="dimmed">/</Text>
            {item.limit ? (
              <Badge
                size="sm"
                variant="light"
                color={item.enrollment_count >= item.limit ? 'red' : 'gray'}
              >
                {item.limit}
              </Badge>
            ) : (
              <Text fz={12} c="dimmed">∞</Text>
            )}
          </Group>
        </Table.Td>

        <Table.Td>
          <Text fz={14}>
            {item.provider_details?.full_name || t("unassigned")}
          </Text>
        </Table.Td>

        <Table.Td>
          <Group gap={8} justify="flex-end">
            <PermissionGate permission="resources.view"><Tooltip label={t("view-enrollments")}><ActionIcon variant="subtle" aria-label={t("view-enrollments")} onClick={() => onViewEnrollments(item)}><IconUsers size={16} /></ActionIcon></Tooltip></PermissionGate>
            <Tooltip label={t("edit")}>
              <PermissionGate permission="resources.edit"><ActionIcon
                variant="subtle"
                onClick={() => onEdit(item)}
              >
                <IconPencil size={16} />
              </ActionIcon></PermissionGate>
            </Tooltip>
            <Tooltip label={t("delete")}>
              <PermissionGate permission="resources.delete"><ActionIcon
                variant="subtle"
                color="red"
                onClick={() => confirmDelete(item._id, item.name)}
              >
                <IconTrash size={16} />
              </ActionIcon></PermissionGate>
            </Tooltip>
          </Group>
        </Table.Td>
      </Table.Tr>
    ));
  }, [data, i18n.language, t, onEdit, onViewEnrollments]);

  return (
    <Table
      withRowBorders
      mt="md"
      mb="xl"
      miw={800}
      withTableBorder
      highlightOnHover
      highlightOnHoverColor="var(--clr-gray-100)">      <Table.Thead>
        <Table.Tr bg="var(--clr-gray-100)">
          <Table.Th>{t("masterclass")}</Table.Th>
          <Table.Th>{t("mode")}</Table.Th>
          <Table.Th>{t("date")}</Table.Th>
          <Table.Th>{t("price")}</Table.Th>
          <Table.Th>{t("enrolled")}</Table.Th>
          <Table.Th>{t("provider")}</Table.Th>
          <Table.Th ta="right">{t("action")}</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {renderLoadingErrorData({
          isLoading: isLoading || isRefetching,
          isError,
          isSuccess,
          dataLength: data?.length || 0,
          emptyDataComponent: (
            <TableEmptyRow colSpan={7} message={t("no-masterclasses")} />
          ),
          errorComponent: (
            <TableErrorRow colSpan={7} message={t("encountered-error")} />
          ),
          loadingComponent: (
            <TableSkeletonRows
              columns={7}
              skeletonProps={{
                widths: ["lg", "sm", "md", "sm", "sm", "lg", "sm"],
                rightAlignLastColumn: true,
              }}
            />
          ),
          dataComponent: rows,
        })}
      </Table.Tbody>
    </Table>
  );
};
